<template>
  <div
    v-if="isSmallScreenDrawerOpen"
    :class="styles.drawerOverlay"
    @click="closeSmallScreenDrawer"
  />

  <aside
    id="app-sidenav"
    :inert="isSmallScreen && !isSmallScreenDrawerOpen"
    :aria-hidden="isSmallScreen && !isSmallScreenDrawerOpen ? 'true' : undefined"
    :class="[
      styles.sideNav,
      !isSmallScreen && !isPinned && !isHoverExpanded && styles.collapsed,
      !isSmallScreen && !isPinned && styles.overlay,
      isSmallScreenDrawerOpen && styles.drawerOpen,
    ]"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div :class="styles.header">
      <div :class="styles.desktopHeader">
        <ShipBobLogo
          :icon-only="isCollapsedView"
          :width="isCollapsedView ? 28 : 130"
          :height="isCollapsedView ? 28 : 34"
          text-color="#fff"
        />
        <button
          v-if="!isSmallScreen && !isCollapsedView"
          v-tooltip.bottom="isPinned ? 'Unpin sidebar' : 'Pin sidebar'"
          type="button"
          :aria-label="isPinned ? 'Unpin sidebar' : 'Pin sidebar'"
          :class="styles.pinButton"
          @click="togglePin"
        >
          <span class="material-icons-round" data-size="2xs" aria-hidden="true">push_pin</span>
        </button>
      </div>
      <div :class="styles.mobileHeader">
        <ShipBobLogo width="130" height="34" text-color="#fff" />
      </div>
    </div>

    <nav :class="styles.nav">
      <template v-for="item in menuItems" :key="item.label">
        <div v-if="item.children?.length" :class="styles.menuGroup">
          <a
            :class="[styles.menuItem, isGroupActive(item) && styles.menuItemActive]"
            href="#"
            role="button"
            :aria-expanded="isGroupOpen(item)"
            @click.prevent="toggleGroup(item)"
          >
            <span class="material-icons-round" :class="styles.menuIcon" data-size="sm">{{
              item.icon
            }}</span>
            <span v-if="showLabels" :class="styles.menuLabel">
              {{ item.label }}
            </span>
            <span
              v-if="showLabels"
              class="material-icons-round"
              :class="styles.menuChevron"
              data-size="sm"
              aria-hidden="true"
              >{{ isGroupOpen(item) ? "expand_less" : "expand_more" }}</span
            >
          </a>
          <div v-if="showLabels && isGroupOpen(item)" :class="styles.menuChildren">
            <template v-for="child in item.children" :key="child.label">
              <router-link v-if="child.route" v-slot="{ navigate, href }" :to="child.route" custom>
                <a
                  :class="[
                    styles.menuItem,
                    styles.menuChild,
                    isChildActive(item, child) && styles.menuItemActive,
                  ]"
                  :href="href"
                  @click="
                    (event) => {
                      navigate(event);
                      closeSmallScreenDrawer();
                    }
                  "
                >
                  <span :class="styles.menuLabel">{{ child.label }}</span>
                </a>
              </router-link>
            </template>
          </div>
        </div>
        <router-link
          v-else-if="item.route"
          v-slot="{ isActive, navigate, href }"
          :to="item.route"
          custom
        >
          <a
            :class="[styles.menuItem, isActive && styles.menuItemActive]"
            :href="href"
            @click="
              (event) => {
                navigate(event);
                closeSmallScreenDrawer();
              }
            "
          >
            <span class="material-icons-round" :class="styles.menuIcon" data-size="sm">{{
              item.icon
            }}</span>
            <span v-if="showLabels" :class="styles.menuLabel">
              {{ item.label }}
            </span>
          </a>
        </router-link>
        <a v-else :class="styles.menuItem" href="#" @click.prevent>
          <span class="material-icons-round" :class="styles.menuIcon" data-size="sm">{{
            item.icon
          }}</span>
          <span v-if="showLabels" :class="styles.menuLabel">
            {{ item.label }}
          </span>
        </a>
      </template>
    </nav>

    <div :class="styles.footer">
      <button type="button" :class="[styles.menuItem, styles.footerButton]" @click="handleLogout">
        <span class="material-icons-round" :class="styles.menuIcon" data-size="sm">logout</span>
        <span v-if="showLabels" :class="styles.menuLabel">Logout</span>
      </button>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { onMounted, computed, reactive } from "vue";
import { useRoute } from "vue-router";
import { useSideNav } from "./useSideNav";
import { defaultMenuItems, type MenuItem } from "./menu";
import ShipBobLogo from "./ShipBobLogo.vue";
import styles from "./SideNav.module.css";
// Relative (not @/) import: reachable from the ./layout expose, whose emitted
// d.ts ships to consumers where the producer's @/ alias can't resolve.
import { useAuthStore } from "../../shared/auth/auth.store";

const {
  isSmallScreen,
  isSmallScreenDrawerOpen,
  isHoverExpanded,
  isPinned,
  initSideNav,
  closeSmallScreenDrawer,
  togglePin,
  onMouseEnter,
  onMouseLeave,
} = useSideNav();

const isCollapsedView = computed(
  () => !isSmallScreen.value && !isPinned.value && !isHoverExpanded.value,
);

const showLabels = computed(() =>
  isSmallScreen.value ? isSmallScreenDrawerOpen.value : isPinned.value || isHoverExpanded.value,
);

const props = defineProps<{
  /** Menu entries; defaults to this host's own menu. */
  menuItems?: MenuItem[];
}>();

const menuItems = computed(() => props.menuItems ?? defaultMenuItems);

// Collapsible groups. A group is open when the user toggled it open, or, until
// they toggle it, when one of its children matches the current route.
const route = useRoute();
const groupOpenState = reactive<Record<string, boolean>>({});

function routeMatches(itemRoute: string | undefined): boolean {
  if (!itemRoute) return false;
  const current = route.path.toLowerCase();
  const target = itemRoute.toLowerCase();
  return current === target || current.startsWith(`${target}/`);
}

function isGroupActive(item: MenuItem): boolean {
  return item.children?.some((child) => routeMatches(child.route)) ?? false;
}

// The active child is the most specific match among its siblings: with
// "User Management" at /user-management next to "My Profile" at
// /user-management/my-profile, router-link's inclusive isActive would light
// both on the profile page (the legacy prefix record is a parent of every
// grafted record under it).
function isChildActive(item: MenuItem, child: MenuItem): boolean {
  if (!child.route || !routeMatches(child.route)) return false;
  const length = child.route.length;
  return !item.children?.some(
    (other) =>
      other !== child && !!other.route && other.route.length > length && routeMatches(other.route),
  );
}

function isGroupOpen(item: MenuItem): boolean {
  return groupOpenState[item.label] ?? isGroupActive(item);
}

function toggleGroup(item: MenuItem) {
  groupOpenState[item.label] = !isGroupOpen(item);
}

const authStore = useAuthStore();

async function handleLogout() {
  try {
    await authStore.logout();
  } catch (error) {
    console.error("[SideNav] Logout failed:", error);
  }
}

onMounted(() => {
  initSideNav();
});
</script>
